import { Box, FormLabel, TextField } from "@mui/material";
import Autocomplete, {
  AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AutoCompleteSelect from "../../../../../components/autocomplete/autoCompleteSelect/AutoCompleteSelect";
import useCarrierConfigurationSelectOptionsQuery from "../../../../../hooks/graphql-hooks/queries/useCarrierConfigurationSelectOptionsQuery";
import { LocationFragment } from "../../../../../models/autogenerated-deliveryService-gql/graphql";
import { IOutboundGeneralInfo } from "../../../../../models/outboundGeneralInfo";
import { isValidString } from "../../../../../utils/helper";
import "./OutboundGeneralInfoForm.scss";

interface CarrierOption {
  id: string;
  label: string;
  value: string;
}

interface OutboundGeneralInfoFormProps {
  formData: IOutboundGeneralInfo;
  onChange: (fieldID: string, text: string) => void;
  locations: LocationFragment[];
}

const OutboundGeneralInfoForm = (props: OutboundGeneralInfoFormProps) => {
  const { t } = useTranslation();

  const { formData, onChange, locations } = props;

  const [carrierOptions, setCarrierOptions] = useState<CarrierOption[]>([]);

  const [carrierQuery] = useCarrierConfigurationSelectOptionsQuery({
    dataAction: (collection) => {
      const options = collection.data.map((carrier: CarrierOption) => ({
        id: carrier.id,
        label: carrier.label,
        value: carrier.value,
      }));
      setCarrierOptions(options);
    },
  });

  useEffect(() => {
    carrierQuery({
      variables: {
        searchString: "",
        page: 1,
        pageSize: 100,
      },
    }).catch(() => {});
  }, [carrierQuery]);

  const locationOptions = locations?.map((l) => l.name).filter(isValidString);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    onChange(id, value);
  };

  const renderTextField = (
    label: keyof IOutboundGeneralInfo,
    required?: boolean,
  ) => (
    <>
      <FormLabel required={required}>{t(label)}</FormLabel>
      <TextField
        data-testid={`outboundGeneralInfo-form-textfield-${label}`}
        id={label}
        className="input-element"
        value={formData[label] || ""}
        onChange={handleChange}
        variant="outlined"
        placeholder={t(label + "Placeholder")}
        required={required}
      />
    </>
  );

  const renderAutoCompleteSelect = (
    label: keyof IOutboundGeneralInfo,
    options: string[],
    required?: boolean,
  ) => (
    <AutoCompleteSelect
      fieldId={label}
      options={options}
      onSelect={onChange}
      initialValue={formData[label] || ""}
      required={required}
    />
  );

  const selectedCarrierLabel =
    carrierOptions.find((carrier) => carrier.id === formData.carrierTenantId)
      ?.label ||
    formData.carrier ||
    "";

  const handleCarrierChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "blur" || reason === "input") return;

    if (reason === "clear") {
      onChange("carrier", "");
      onChange("carrierTenantId", "");
      return;
    }

    const selectedCarrier = carrierOptions.find(
      (carrier) => carrier.label === value,
    );

    onChange("carrier", selectedCarrier?.label || "");
    onChange("carrierTenantId", selectedCarrier?.id || "");
  };

  return (
    <Box
      className="form-flex-container"
      component="form"
      sx={{ width: "100%" }}
      autoComplete="off"
    >
      {renderTextField("deliveryNumber", true)}
      {renderAutoCompleteSelect("location", locationOptions || [], true)}
      {renderTextField("recipient", true)}
      {renderTextField("sender")}
      <Autocomplete
        options={carrierOptions.map((carrier) => carrier.label)}
        id="auto-complete-select-carrier"
        autoComplete
        value={selectedCarrierLabel}
        blurOnSelect
        includeInputInList
        onInputChange={handleCarrierChange}
        renderInput={(params) => (
          <>
            <FormLabel>{t("carrier")}</FormLabel>
            <TextField
              {...params}
              data-testid="auto-complete-select-carrier"
              className="input-element"
              id="carrier"
              variant="outlined"
              placeholder={t("selectPlaceholder")}
            />
          </>
        )}
      />
    </Box>
  );
};
export default OutboundGeneralInfoForm;
